import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, ApiError, setUnauthorizedHandler, type User } from '../api.ts'

interface AuthState {
  user: User | null
  loading: boolean
  login: (code: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  // Start in loading: we don't yet know if there's a live session cookie.
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Any 401 from anywhere means the session is gone — drop back to the login
    // screen instead of leaving a dead page with an error box on it.
    setUnauthorizedHandler(() => setUser(null))

    api
      .get<{ user: User }>('/api/me')
      .then((r) => setUser(r.user))
      .catch(() => setUser(null)) // 401 = not logged in, which is fine
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (code: string, password: string) => {
    const r = await api.post<{ user: User }>('/api/login', { code, password })
    setUser(r.user)
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/api/logout')
    } catch (e) {
      // A network failure on logout shouldn't trap the user in the app.
      if (!(e instanceof ApiError)) throw e
    }
    // Wipe the offline snapshot so the next user on a shared field laptop doesn't
    // find the previous one's clients and drafts (imported lazily to keep Dexie
    // out of the auth module's dependency graph).
    try {
      const { clearOfflineData } = await import('../offline/db.ts')
      await clearOfflineData()
    } catch {
      /* Dexie unavailable — nothing to clear. */
    }
    setUser(null)
  }, [])

  return <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth musi być wewnątrz AuthProvider')
  return ctx
}
